import React from 'react';
import {
  MdOutlineMarkEmailUnread,
  MdMarkEmailUnread,
  MdDeleteOutline,
} from 'react-icons/md';
import TrashRestore from '@/components/icons/TrashRestore';
import TrashRestoreOutline from '@/components/icons/TrashRestoreOutline';
import { useFileActions } from '../../../../../../hooks/useFileActions';
import type { Table } from '@tanstack/react-table';
import type { File } from '../../../../types/file.types';

interface BulkActionsProps {
  table: Table<File>;
}

export const BulkActions: React.FC<BulkActionsProps> = ({ table }) => {
  const { toggleReadMulti, toggleArchiveMulti, deleteMulti, isLoading } =
    useFileActions();
  const selectedRows = table.getSelectedRowModel().rows;
  const selectedFiles = selectedRows.map((row) => row.original);
  const someSelected = selectedFiles.length > 0;
  const allRead = selectedFiles.every((file) => file.lu);
  const allArchived = selectedFiles.every((file) => file.archive);

  const handleMarkAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLoading) {
      const fileIds = selectedFiles.map((file) => file.id);
      await toggleReadMulti(fileIds, allRead);
      table.resetRowSelection();
    }
  };

  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLoading) {
      const fileIds = selectedFiles.map((file) => file.id);
      await toggleArchiveMulti(fileIds, allArchived);
      table.resetRowSelection();
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Voulez-vous vraiment supprimer ces fichiers ?')) {
      if (!isLoading) {
        const fileIds = selectedFiles.map((file) => file.id);
        await deleteMulti(fileIds);
        table.resetRowSelection();
      }
    }
  };

  if (!someSelected) return null;

  return (
    <div className="flex items-center gap-2">
      <span className="mr-2 text-sm text-gray-600 dark:text-gray-400">
        {selectedFiles.length} fichier{selectedFiles.length > 1 ? 's' : ''}{' '}
        sélectionné{selectedFiles.length > 1 ? 's' : ''}
      </span>

      <button
        onClick={handleMarkAsRead}
        disabled={isLoading}
        className={`flex h-8 items-center gap-2 rounded-md bg-white px-3 text-[#2f2f2f] shadow-sm transition-all duration-300 hover:bg-[#eaeaed] dark:bg-[#333135] dark:text-white dark:hover:bg-[#545454] ${
          isLoading ? 'cursor-not-allowed opacity-50' : ''
        }`}
        data-tooltip-content={
          allRead ? 'Marquer comme non lu' : 'Marquer comme lu'
        }
        data-tooltip-id="tooltip"
        data-tooltip-place="bottom"
        data-tooltip-delay-show={100}
        data-tooltip-offset={5}
      >
        {allRead ? (
          <MdMarkEmailUnread className="text-lg" />
        ) : (
          <MdOutlineMarkEmailUnread className="text-lg" />
        )}
      </button>

      <button
        onClick={handleArchive}
        disabled={isLoading}
        className={`flex h-8 items-center gap-2 rounded-md bg-white px-3 text-[#2f2f2f] shadow-sm transition-all duration-300 hover:bg-[#eaeaed] dark:bg-[#333135] dark:text-white dark:hover:bg-[#545454] ${
          isLoading ? 'cursor-not-allowed opacity-50' : ''
        }`}
        data-tooltip-content={allArchived ? 'Désarchiver' : 'Archiver'}
        data-tooltip-id="tooltip"
        data-tooltip-place="bottom"
        data-tooltip-delay-show={100}
        data-tooltip-offset={5}
      >
        {allArchived ? (
          <TrashRestore className="text-lg" />
        ) : (
          <TrashRestoreOutline className="text-lg" />
        )}
      </button>

      {allArchived && (
        <button
          onClick={handleDelete}
          disabled={isLoading}
          className={`flex h-8 items-center gap-2 rounded-md bg-white px-3 text-[red] shadow-sm transition-all duration-300 hover:bg-[#eaeaed] dark:bg-[#333135] dark:hover:bg-[#545454] ${
            isLoading ? 'cursor-not-allowed opacity-50' : ''
          }`}
          data-tooltip-content="Supprimer"
          data-tooltip-id="tooltip"
          data-tooltip-place="bottom"
          data-tooltip-delay-show={100}
          data-tooltip-offset={5}
        >
          <MdDeleteOutline className="text-lg" />
        </button>
      )}
    </div>
  );
};
